import React, { Component, Fragment } from 'react';
import AeMaxPrice from '~/AeMaxPrice';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import BgMaxPrice from '~/BgMaxPrice';
import CzMaxPrice from '~/CzMaxPrice';
import CzReimbursement, {
	GroupCalculation as CzReimbursementGroupCalculation,
	GroupList as CzReimbursementGroupList,
	Settings as CzReimbursementSettings,
} from '~/CzReimbursement';
import HrMaxPrice from '~/HrMaxPrice';
import LtMaxPrice from '~/LtMaxPrice';
import ProductGroupHistoryModal from '~/ProductGroupHistoryModal';
import ProductGroupList from '~/ProductGroupList';
import ProductGroupNotificationSettingsModal from '~/ProductGroupNotificationSettingsModal';
import RoMaxPrice, { Settings as RoMaxPriceSettings } from '~/RoMaxPrice';
import SiMaxPrice from '~/SiMaxPrice';
import SkMaxPrice, { Settings as SkMaxPriceSettings } from '~/SkMaxPrice';
import ActivityIndicator from '~/UI/ActivityIndicator';
import NotFoundErrorPage from '~/UI/NotFoundErrorPage/NotFoundErrorPage';

import Header from './Header/Header';
import Modal, { Name as ModalName, Names as ModalNames } from './Modal/Modal';
import ProductList from './ProductListContainer';
import styles from './styles.less';
interface Props {
	groupId: string | null;
	group: UserProductGroup | null;
	groupNotFound: boolean;
	isFetchingGroup: boolean;
	isUpdatingNotification: boolean;
	currency: string | null;
	priceType: string | null;
	filterCalculatedPrice: boolean;
	productId: string | null;
	productTabName?: string;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	showNotificationSettings: boolean;
	showHistory: boolean;
	formatDate(date: Date, includeYear?: boolean, locale?: string[]): string;
	onFetch(): void;
	onPrune(): void;
	onCloseDetail(): void;
	onShowDetail(productId: string, tabName: string): void;
	onRemoved(group: UserProductGroup): void;
	onQuery(a: string | null, b: string | null, c: string | null, d: boolean, e: boolean, f: boolean): void;
	onProductPriceTypeChanged(): void;
}

interface State {
	modalName: ModalName | null;
	changesCount: number;
}

export default class DetailPage extends Component<Props, State> {
	static defaultProps = {
		groupId: null,
		group: null,
		currency: null,
		priceType: null,
		productId: null,
		productTabName: 'info',
	};

	state = {
		modalName: null,
		changesCount: 0,
	};

	componentDidMount() {
		this.props.onFetch();
	}

	componentWillUnmount() {
		this.props.onPrune();
	}

	componentDidUpdate(prevProps: Props) {
		const { groupId, onFetch, onPrune } = this.props;

		if (prevProps.groupId !== groupId) {
			onPrune();
			onFetch();
			this.setState({ modalName: null });
		}
	}

	renderHeader(group: UserProductGroup) {
		const { isUpdatingNotification, currency, priceType, filterCalculatedPrice, productId, formatDate, onQuery } =
			this.props;

		return (
			<Header
				currency={currency}
				formatDate={formatDate}
				group={group}
				isUpdatingNotification={isUpdatingNotification}
				onCopy={() => this.setState({ modalName: ModalNames.copy })}
				onCreateListClick={() => this.setState({ modalName: ModalNames.create })}
				onDelete={() => this.setState({ modalName: ModalNames.delete })}
				onExchangeRateClick={() => this.setState({ modalName: ModalNames.exchangeRate })}
				onHistoryClick={() => onQuery(productId, currency, priceType, false, filterCalculatedPrice, true)}
				onNotificationSettingsClick={() => onQuery(productId, currency, priceType, true, filterCalculatedPrice, false)}
				onRename={() => this.setState({ modalName: ModalNames.rename })}
			/>
		);
	}

	renderGroup(group: UserProductGroup) {
		const { changesCount } = this.state;

		return (
			<Fragment>
				{this.renderHeader(group)}
				{group.isGroup() && group.hasCzReimbursementModule() ? (
					<React.Fragment key={`settings-calc-${changesCount}`}>
						<CzReimbursementGroupCalculation groupId={group.id} />
						<CzReimbursementSettings group={group} />
					</React.Fragment>
				) : null}
				{group.isGroup() && group.hasSkMaxPriceModule() ? <SkMaxPriceSettings group={group} /> : null}
				{group.isGroup() && group.hasRoMaxPriceModule() ? <RoMaxPriceSettings group={group} /> : null}
				{group.isGroup() && group.hasCzReimbursementModule() ? (
					<CzReimbursementGroupList
						key={`group-list-${changesCount}`}
						parentId={group.id}
						onChange={() => {
							this.setState((state) => ({
								changesCount: state.changesCount + 1,
							}));
						}}
					/>
				) : (
					<ProductGroupList parentId={group.id} />
				)}
			</Fragment>
		);
	}

	renderProductList(group: UserProductGroup) {
		const {
			currency,
			productId,
			productTabName,
			productDetailLink,
			priceType,
			filterCalculatedPrice,
			onShowDetail,
			onCloseDetail,
		} = this.props;

		if (group.hasCzMaxPriceModule()) {
			return (
				<CzMaxPrice
					currency={currency}
					detailProductId={productId}
					detailProductTabName={productTabName}
					group={group}
					header={this.renderHeader(group)}
					key={group.id}
					productDetailLink={productDetailLink}
					onCloseDetail={onCloseDetail}
					onCurrencyChange={(currency) => this.handlePriceChange(currency, priceType, filterCalculatedPrice)}
					onShowDetail={onShowDetail}
				/>
			);
		}
		if (group.hasCzReimbursementModule()) {
			return (
				<CzReimbursement
					currency={currency}
					detailProductId={productId}
					detailProductTabName={productTabName}
					group={group}
					header={this.renderHeader(group)}
					key={group.id}
					productDetailLink={productDetailLink}
					onCloseDetail={onCloseDetail}
					onCurrencyChange={(currency) => this.handlePriceChange(currency, priceType, filterCalculatedPrice)}
					onShowDetail={onShowDetail}
				/>
			);
		}
		if (group.hasSkMaxPriceModule()) {
			return (
				<SkMaxPrice
					currency={currency}
					detailProductId={productId}
					detailProductTabName={productTabName}
					group={group}
					header={this.renderHeader(group)}
					key={group.id}
					productDetailLink={productDetailLink}
					onCloseDetail={onCloseDetail}
					onCurrencyChange={(currency) => this.handlePriceChange(currency, priceType, filterCalculatedPrice)}
					onShowDetail={onShowDetail}
				/>
			);
		}
		if (group.hasAeMaxPriceModule()) {
			return (
				<AeMaxPrice
					currency={currency}
					detailProductId={productId}
					detailProductTabName={productTabName}
					group={group}
					header={this.renderHeader(group)}
					key={group.id}
					productDetailLink={productDetailLink}
					onCloseDetail={onCloseDetail}
					onCurrencyChange={(currency) => this.handlePriceChange(currency, priceType, filterCalculatedPrice)}
					onShowDetail={onShowDetail}
				/>
			);
		}
		if (group.hasSiMaxPriceModule()) {
			return (
				<SiMaxPrice
					currency={currency}
					detailProductId={productId}
					detailProductTabName={productTabName}
					group={group}
					header={this.renderHeader(group)}
					key={group.id}
					productDetailLink={productDetailLink}
					referenceBasketVersion={null}
					onCloseDetail={onCloseDetail}
					onCurrencyChange={(currency) => this.handlePriceChange(currency, priceType, filterCalculatedPrice)}
					onShowDetail={onShowDetail}
				/>
			);
		}
		if (group.hasHrMaxPriceModule()) {
			return (
				<HrMaxPrice
					currency={currency}
					detailProductId={productId}
					detailProductTabName={productTabName}
					group={group}
					header={this.renderHeader(group)}
					key={group.id}
					productDetailLink={productDetailLink}
					onCloseDetail={onCloseDetail}
					onCurrencyChange={(currency) => this.handlePriceChange(currency, priceType, filterCalculatedPrice)}
					onShowDetail={onShowDetail}
				/>
			);
		}
		if (group.hasBgMaxPriceModule()) {
			return (
				<BgMaxPrice
					currency={currency}
					detailProductId={productId}
					detailProductTabName={productTabName}
					group={group}
					header={this.renderHeader(group)}
					key={group.id}
					productDetailLink={productDetailLink}
					onCloseDetail={onCloseDetail}
					onCurrencyChange={(currency) => this.handlePriceChange(currency, priceType, filterCalculatedPrice)}
					onShowDetail={onShowDetail}
				/>
			);
		}
		if (group.hasRoMaxPriceModule()) {
			return (
				<RoMaxPrice
					currency={currency}
					detailProductId={productId}
					detailProductTabName={productTabName}
					group={group}
					header={this.renderHeader(group)}
					key={group.id}
					productDetailLink={productDetailLink}
					onCloseDetail={onCloseDetail}
					onCurrencyChange={(currency) => this.handlePriceChange(currency, priceType, filterCalculatedPrice)}
					onShowDetail={onShowDetail}
				/>
			);
		}
		if (group.hasLtMaxPriceModule()) {
			return (
				<LtMaxPrice
					currency={currency}
					detailProductId={productId}
					detailProductTabName={productTabName}
					group={group}
					header={this.renderHeader(group)}
					key={group.id}
					productDetailLink={productDetailLink}
					onCloseDetail={onCloseDetail}
					onCurrencyChange={(currency) => this.handlePriceChange(currency, priceType, filterCalculatedPrice)}
					onShowDetail={onShowDetail}
				/>
			);
		}

		return (
			<ProductList
				currency={currency}
				detailProductId={productId}
				detailProductTabName={productTabName}
				filterCalculatedPrice={filterCalculatedPrice}
				group={group}
				header={this.renderHeader(group)}
				priceType={priceType}
				productDetailLink={productDetailLink}
				onCloseDetail={onCloseDetail}
				onPriceChanged={this.handlePriceChange}
				onShowDetail={onShowDetail}
			/>
		);
	}

	handlePriceChange = (currency: string | null, priceType: string | null, filterCalculatedPrice: boolean) => {
		const {
			priceType: currentPriceType,
			productId,
			showNotificationSettings,
			showHistory,
			onQuery,
			onProductPriceTypeChanged,
		} = this.props;
		onQuery(productId, currency, priceType, showNotificationSettings, filterCalculatedPrice, showHistory);
		if (currentPriceType !== priceType) {
			onProductPriceTypeChanged();
		}
	};

	deleted = (group: UserProductGroup) => {
		this.setState({ modalName: null });
		this.props.onRemoved(group);
	};

	render() {
		const { groupId, group, groupNotFound, isFetchingGroup } = this.props;
		if (!groupId || groupNotFound) {
			return <NotFoundErrorPage />;
		}
		if (isFetchingGroup) {
			return <ActivityIndicator />;
		}
		if (!group) {
			return null;
		}

		const {
			props: { currency, priceType, filterCalculatedPrice, productId, showNotificationSettings, showHistory, onQuery },
			state: { modalName },
		} = this;

		return (
			<Fragment>
				{modalName ? (
					<Modal
						group={group}
						name={modalName}
						onClose={() => this.setState({ modalName: null })}
						onCreate={() => {
							this.setState((state) => ({
								changesCount: state.changesCount + 1,
							}));
						}}
						onDelete={this.deleted}
					/>
				) : null}

				{showNotificationSettings ? (
					<ProductGroupNotificationSettingsModal
						group={group}
						onClose={() => onQuery(productId, currency, priceType, false, filterCalculatedPrice, showHistory)}
						onChange={() => onQuery(productId, currency, priceType, false, filterCalculatedPrice, showHistory)}
					/>
				) : null}

				{showHistory ? (
					<ProductGroupHistoryModal
						group={group}
						onClose={() => onQuery(productId, currency, priceType, false, filterCalculatedPrice, false)}
					/>
				) : null}

				<section className={styles.section}>
					{group.isGroup() ? this.renderGroup(group) : this.renderProductList(group)}
				</section>
			</Fragment>
		);
	}
}
